import React, { memo, useMemo, useState, useEffect, useCallback } from "react";
import { Star, Eye, Bookmark, UserCheck, Award, FileText } from "lucide-react";
import "../styles/tender.css";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const API_BASE_URL = `${process.env.REACT_APP_BASE_API_URL}`;

const openInNewTab = (url) => {
  const w = window.open(url, "_blank");
  if (w) {
    try {
      w.opener = window;
    } catch (e) {}
  }
};

const persistTenderState = (tenderId, state) => {
  try {
    sessionStorage.setItem(`tender_state_${tenderId}`, JSON.stringify(state));
  } catch (e) {}
};

const TenderCard = ({
  tender,
  mode = "listing",
  forceView = false,
  onToggleFavourite,
  openAuthModal,
  onMouseEnter,
  prefetchedData,
  onTenderClick
}) => {
  const { user, role, isAuthenticated } = useAuth();

  const safeTender = tender || {};
  const data = tender || {};
  const tenderId =
    data.tender_id ||
    data.id ||
    data.portal_tender_id ||
    data.tender_number ||
    data._id;
  const applicationStatus = data.applicationStatus;
  const deadlineStr = data.submission_deadline || data.endDate;

  const [isFavourite, setIsFavourite] = useState(() => {
    const id =
      safeTender.tender_id || safeTender.id || safeTender.portal_tender_id;
    if (id) {
      try {
        const sessionFav = sessionStorage.getItem(`fav_status_${id}`);
        if (sessionFav !== null) return sessionFav === "true";
      } catch (e) {}
    }
    return safeTender.isFavourite || safeTender.is_favourite || false;
  });

  const views =
    safeTender.views || safeTender.view_count || safeTender.stats?.views || 0;
  const favouritesCount =
    safeTender.favourites_count || safeTender.favouritesCount || 0;
  const applies =
    safeTender.applies ||
    safeTender.apply_count ||
    safeTender.stats?.applies ||
    0;

  const updateFavouriteStorage = useCallback((id, isFav) => {
    if (!id) return;
    try {
      sessionStorage.setItem(`fav_status_${id}`, isFav);
    } catch (e) {}
  }, []);

  const isAdmin = role === "admin";
  const isOperations = role === "operations";
  const isSales = role === "sales";
  const isCustomer = role === "customer" || role === "user";

  const isAwardedTender =
    mode === "result" || data.status === "Awarded" || data.evaluation_count > 0;

  const deadlineStyle = useMemo(() => {
    if (!deadlineStr) return {};
    const deadline = new Date(deadlineStr);
    const now = new Date();
    const diffMs = deadline.getTime() - now.getTime();
    if (diffMs < 0) return { color: "#858585" };
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    if (diffDays > 10) return { color: "#27ae60" };
    if (diffDays > 3 && diffDays <= 10) return { color: "#f97316" };
    if (diffDays > 1 && diffDays <= 3) return { color: "#DC2626" };
    return { color: "#991b1b", fontWeight: "bold" };
  }, [deadlineStr]);

  const submissionDeadlineFormatted = useMemo(() => {
    if (!deadlineStr) return null;
    const deadline = new Date(deadlineStr);
    const now = new Date();
    const diffMs = deadline.getTime() - now.getTime();
    const dateStr = deadline.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
    if (diffMs < 0) return `${dateStr} (Expired)`;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHours = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHours / 24);
    const remainingHours = diffHours % 24;
    const remainingMinutes = diffMin % 60;
    const displayParts = [];
    if (diffDays > 0)
      displayParts.push(`${diffDays} day${diffDays > 1 ? "s" : ""}`);
    if (remainingHours > 0 || diffDays === 0)
      displayParts.push(`${remainingHours}h`);
    if (remainingMinutes > 0 || (diffDays === 0 && remainingHours === 0))
      displayParts.push(`${remainingMinutes}m`);
    if (displayParts.length === 0) return `${dateStr} (Less than a minute)`;
    return `${dateStr} (${displayParts.join(" ")} left)`;
  }, [deadlineStr]);

  const rawTitle = useMemo(() => {
    return data.title || data.items || data.bidNumber || "N/A";
  }, [data]);

  const displayTitle = useMemo(() => {
    if (isOperations) return rawTitle;
    return rawTitle.length > 150
      ? rawTitle.substring(0, 150) + "..."
      : rawTitle;
  }, [rawTitle, isOperations]);

  const buttonText = useMemo(() => {
    if (isAwardedTender) return "View";
    if (isAdmin || isSales || isOperations || forceView || mode === "archive")
      return "View";
    if (data.isApplied || applicationStatus === "requested") return "Requested";
    if (applicationStatus === "rejected") return "Request Reopen";
    if (data.status === "Archived" || data.status === "archived")
      return "Archived";
    return "View";
  }, [
    isAwardedTender,
    isAdmin,
    isSales,
    isOperations,
    forceView,
    mode,
    data.isApplied,
    applicationStatus,
    data.status
  ]);

  const isButtonDisabled = useMemo(() => {
    return (
      !(isCustomer || isAdmin || isSales || isOperations) ||
      (isCustomer &&
        (data.isApplied ||
          applicationStatus === "requested" ||
          data.status === "Archived" ||
          data.status === "archived"))
    );
  }, [
    isCustomer,
    isAdmin,
    isSales,
    isOperations,
    data.isApplied,
    applicationStatus,
    data.status
  ]);

  const formatLocation = (location) => {
    if (!location) return "-";
    if (typeof location !== "string") return location;
    if (location === location.toUpperCase()) {
      return location
        .split(" ")
        .map(
          (word) =>
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
    }
    return location;
  };

  const isNewTender = useMemo(() => {
    if (!data.published_date) return false;
    const publishedDate = new Date(data.published_date);
    const now = new Date();
    const diffDays = (now - publishedDate) / (1000 * 60 * 60 * 24);
    return diffDays <= 7;
  }, [data.published_date]);

  const displayStatus = useMemo(() => {
    if (mode === "result") return "Awarded";
    if (isNewTender && (mode === "listing" || mode === "search")) return "New";
    if (mode === "archive") return "Expired";
    if (data?.status) {
      const normalized = data.status.toLowerCase();
      if (normalized === "archived" || normalized === "expired")
        return "Expired";
      return (
        data.status.charAt(0).toUpperCase() +
        data.status.slice(1).toLowerCase()
      );
    }
    return "N/A";
  }, [mode, isNewTender, data?.status]);

  useEffect(() => {
    const id = String(tenderId);
    if (!id) return;
    try {
      const sessionFav = sessionStorage.getItem(`fav_status_${id}`);
      if (sessionFav !== null) {
        setIsFavourite(sessionFav === "true");
        return;
      }
    } catch (e) {}
    const propFav = data.isFavourite || data.is_favourite;
    setIsFavourite(!!propFav);
  }, [data.isFavourite, data.is_favourite, tenderId]);

  useEffect(() => {
    const handleFavouritesSync = (e) => {
      const detail = e.detail;
      if (detail && String(detail.tenderId) === String(tenderId)) {
        setIsFavourite(detail.isFavourite);
        updateFavouriteStorage(tenderId, detail.isFavourite);
      }
    };
    window.addEventListener("favourites_sync", handleFavouritesSync);
    return () =>
      window.removeEventListener("favourites_sync", handleFavouritesSync);
  }, [tenderId, updateFavouriteStorage]);

  const goToTender = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!isAuthenticated) {
      openAuthModal?.();
      return;
    }

    persistTenderState(tenderId, {
      sourceTab: mode,
      initialIsFavourite: isFavourite
    });

    if (onTenderClick) {
      onTenderClick(tenderId);
      return;
    }

    if (mode === "result") {
      openInNewTab(`/results/${tenderId}`);
    } else {
      openInNewTab(`/tender/${tenderId}`);
    }
  };

  const handleAwardedClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      openAuthModal?.();
      return;
    }
    openInNewTab(`/Tender-Awarded/${tenderId}`);
  };

  const handleTechnicalClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      openAuthModal?.();
      return;
    }
    openInNewTab(`/Tender-Awarded2/${tenderId}`);
  };

const handleFavouriteToggle = async (e) => {
  e.preventDefault();
  e.stopPropagation();
  if (!isAuthenticated) {
    openAuthModal?.();
    return;
  }

  // ✅ 1. Optimistically flip the star IMMEDIATELY
  const previousState = isFavourite;
  const optimisticState = !previousState;

  setIsFavourite(optimisticState);
  updateFavouriteStorage(tenderId, optimisticState);

  // ✅ 2. Broadcast to other cards on the page instantly
  window.dispatchEvent(
    new CustomEvent("favourites_sync", {
      detail: { tenderId: String(tenderId), isFavourite: optimisticState }
    })
  );

  // ✅ 3. Show toast immediately
  if (optimisticState) toast.success("Added to favourites ⭐");
  else toast.info("Removed from favourites");

  // ✅ 4. Fire the API in the background
  try {
    const res = await axios.post(`${API_BASE_URL}/api/favourites/toggle`, {
      userId: user?.id || user?._id,
      tenderId: tenderId
    });

    const serverStatus = res.data.isFavourite;

    // If server disagrees with optimistic (rare — race condition), correct it
    if (serverStatus !== optimisticState) {
      setIsFavourite(serverStatus);
      updateFavouriteStorage(tenderId, serverStatus);
      window.dispatchEvent(
        new CustomEvent("favourites_sync", {
          detail: { tenderId: String(tenderId), isFavourite: serverStatus }
        })
      );
    }

    if (onToggleFavourite) onToggleFavourite(tenderId, serverStatus);
  } catch (err) {
    console.error(err);

    // ❌ 5. Revert on failure
    setIsFavourite(previousState);
    updateFavouriteStorage(tenderId, previousState);
    window.dispatchEvent(
      new CustomEvent("favourites_sync", {
        detail: { tenderId: String(tenderId), isFavourite: previousState }
      })
    );

    toast.error("Failed to update favourite. Please try again.");
  }
};

  const StatIndicator = ({ icon: Icon, value, label }) => (
    <span className="tender-stat-item">
      <Icon size={16} className="tender-stat-icon" />
      <span className="tender-stat-value fw-semibold">{value}</span>
      {label && <span className="tender-stat-label">{label}</span>}
    </span>
  );

  const InfoField = ({ label, value }) => {
    const displayValue = value || value === 0 ? value : "-";
    return (
      <div>
        <div className="tender-info-label">{label}</div>
        <div className="tender-info-value">{displayValue}</div>
      </div>
    );
  };

  const formatCurrency = (value) => {
    if (!value || value === "-") return "-";
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    if (num < 1000) return "N/A";
    return `₹ ${num.toLocaleString("en-IN")}`;
  };

  const getStatusClass = () => {
    const status = data?.status?.toLowerCase();
    if (status === "live") return "live";
    if (status === "archived") return "archived";
    if (status === "closing soon" || status === "closing_soon") return "closing";
    if (status === "expired") return "expired";
    return "";
  };

  const formatExemptionValue = (value) => {
    if (!value || value === "-" || value === null || value === undefined)
      return "-";
    if (typeof value === "string") {
      const lower = value.toLowerCase();
      if (lower === "yes") return "Yes";
      if (lower === "no") return "No";
      return value;
    }
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return "-";
  };

  const FavouriteStar = ({ size, isFavourite, onClick, className }) => {
    const [isHovered, setIsHovered] = useState(false);
    const starStyles = {
      cursor: "pointer",
      transition: "all 0.2s ease",
      transform: isHovered ? "scale(1.15)" : "scale(1)",
      color: isFavourite ? "#FACC15" : isHovered ? "#FACC15" : "#9ca3af",
      fill: isFavourite ? "#FACC15" : isHovered ? "#FACC15" : "none"
    };
    return (
      <Star
        size={size}
        style={starStyles}
        className={className}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={onClick}
        title={isFavourite ? "Remove from favourites" : "Add to favourites"}
      />
    );
  };

  const getDisplayStatusClass = () => {
    if (displayStatus === "New") return "new";
    if (displayStatus === "Expired") return "expired";
    if (displayStatus === "Live") return "live";
    if (displayStatus === "Closing Soon") return "closing";
    if (displayStatus === "Awarded") return "awarded";
    return getStatusClass();
  };

  if (!tender || typeof tender !== "object") return null;

  return (
    <div className="tender-container" onMouseEnter={onMouseEnter}>
      <div
        className="tender-card"
        style={{ cursor: "pointer" }}
        onClick={goToTender}
      >
        <div className="tender-header">
          <div className="d-flex gap-3 align-items-start flex-wrap">
            <h2 className="tender-title" title={rawTitle}>
              {displayTitle}
            </h2>
            {mode !== "shortlisted" && (
              <span className={`tender-status-badge ${getDisplayStatusClass()}`}>
                <span className="tender-badge-dot"></span>
                {displayStatus}
              </span>
            )}
          </div>
          <div className="tender-header-actions">
            {mode === "shortlisted" && data.shortlistSummary && (
              <>
                {data.shortlistSummary.technicalStatus?.toLowerCase() ===
                  "qualified" && (
                  <span className="shortlist-badge technical">
                    Technical Qualified
                  </span>
                )}
                {data.shortlistSummary.rank && (
                  <span className="shortlist-badge badge-rank">
                    {data.shortlistSummary.rank}
                  </span>
                )}
              </>
            )}

            {mode === "applied" && data.applicationStatusLabel && (
              <span
                className={`application-status-badge ${data.applicationStatus
                  ?.toLowerCase()
                  .replace(/\s+/g, "-")}`}
              >
                {data.applicationStatusLabel}
              </span>
            )}

            {mode === "favourite" && !isAwardedTender && (
              <FavouriteStar
                size={20}
                isFavourite={isFavourite}
                onClick={handleFavouriteToggle}
                className="tender-icon"
              />
            )}

            {!isAdmin && ["listing", "search", "archive"].includes(mode) && (
              <FavouriteStar
                size={20}
                isFavourite={isFavourite}
                onClick={handleFavouriteToggle}
                className="tender-icon"
              />
            )}
          </div>
        </div>

        <div className="tender-info-grid">
          <InfoField
            label="Department"
            value={data.department || data.organisationName || "-"}
          />
          <InfoField
            label="Ministry"
            value={data.ministry || data.ministryName || "-"}
          />
          <InfoField
            label="Location"
            value={formatLocation(data.location || "-")}
          />
          <InfoField
            label="Tender Value"
            value={formatCurrency(data.tender_value || data.bidValue)}
          />
          <InfoField
            label="EMD"
            value={formatCurrency(
              data.emd_amount || data.emdValue || data.emd_value
            )}
          />
        </div>

        <div className="tender-footer">
          <div className="tender-footer-left">
            <div>
              <div className="tender-info-label">SUBMISSION DEADLINE</div>
              <div className="tender-deadline-wrapper">
                <span className="tender-deadline-date" style={deadlineStyle}>
                  {submissionDeadlineFormatted || "-"}
                </span>
              </div>
            </div>
            <InfoField
              label="MSE Exemption"
              value={formatExemptionValue(data.mse_exemption)}
            />
            <InfoField
              label="Startup Exemption"
              value={formatExemptionValue(data.startup_exemption)}
            />
          </div>

          <div className="tender-footer-right">
            {!isAwardedTender && (
              <div className="tender-stats-container">
                <StatIndicator icon={Eye} value={views} />
                <StatIndicator
                  icon={Bookmark}
                  value={favouritesCount}
                  label=""
                />
                <StatIndicator icon={UserCheck} value={applies} label="Applied" />
              </div>
            )}

            <div className="tender-actions">
              {isAwardedTender ? (
                <div className="d-flex gap-2 align-items-center">
                  <button
                    type="button"
                    className="tender-apply-button awarded-btn"
                    onClick={handleAwardedClick}
                    style={{
                      backgroundColor: "#2F7A87",
                      color: "white",
                      border: "none",
                      borderRadius: "50px",
                      padding: "6px 14px",
                      fontSize: "12px",
                      fontWeight: "600",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      transition: "all 0.2s ease"
                    }}
                    title="View Awarded Details"
                  >
                    <Award size={14} />
                    Awarded Status
                  </button>

                  <button
                    type="button"
                    className="tender-apply-button technical-btn"
                    onClick={handleTechnicalClick}
                    style={{
                      backgroundColor: "transparent",
                      color: "#2F7A87",
                      border: "1.5px solid #2F7A87",
                      borderRadius: "50px",
                      padding: "6px 14px",
                      fontSize: "12px",
                      fontWeight: "600",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      transition: "all 0.2s ease"
                    }}
                    title="View Technical Evaluation"
                  >
                    <FileText size={14} />
                    Techical Status
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className={`tender-apply-button ${
                    data.isApplied || applicationStatus === "requested"
                      ? "requested-btn"
                      : ""
                  }`}
                  disabled={isButtonDisabled}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isButtonDisabled) return;
                    goToTender(e);
                  }}
                >
                  {buttonText}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(TenderCard);